import React, { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { SectionLabel, MaskedHeading, Reveal } from "@/components/site/Motion";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { createReservation } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-brand text-white placeholder:text-white/35 outline-none py-3 transition-colors font-body";
const labelCls = "text-xs uppercase tracking-widest text-white/55 mb-2 block font-body";

export const Reservation = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", guests: "", occasion: "", notes: "" });
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !date || !form.guests) {
      toast.error(t.reserve.invalid);
      return;
    }
    setLoading(true);
    try {
      await createReservation({
        name: form.name,
        phone: form.phone,
        date: format(date, "yyyy-MM-dd"),
        guests: parseInt(form.guests, 10),
        occasion: form.occasion || null,
        notes: form.notes || null,
      });
      toast.success(t.reserve.success, { icon: <Check size={16} /> });
      setForm({ name: "", phone: "", guests: "", occasion: "", notes: "" });
      setDate(null);
    } catch {
      toast.error(t.reserve.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reserve" className="py-24 md:py-36 px-6 md:px-12 relative overflow-hidden">
      <div className="absolute right-0 top-1/4 w-[500px] h-[500px] ambient-glow blur-3xl pointer-events-none opacity-60" />
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 relative">
        <div className="lg:col-span-4">
          <SectionLabel>{t.reserve.overline}</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl text-white tracking-tight leading-[1.02]">
            <MaskedHeading text={t.reserve.title} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 font-body text-white/60 leading-relaxed max-w-sm">{t.reserve.desc}</p>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.15}>
            <form data-testid="reservation-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className={labelCls}>{t.reserve.name} *</label>
                <input data-testid="res-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="—" />
              </div>
              <div>
                <label className={labelCls}>{t.reserve.phone} *</label>
                <input data-testid="res-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+90 ___ ___ __ __" />
              </div>

              <div>
                <label className={labelCls}>{t.reserve.date} *</label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="res-date-trigger"
                      className={`${inputCls} flex items-center justify-between text-left ${date ? "text-white" : "text-white/35"}`}
                    >
                      {date ? format(date, "dd MMM yyyy") : t.reserve.pickDate}
                      <CalendarIcon size={16} className="text-brand" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-surface border-white/15" align="start">
                    <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setDateOpen(false); }} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className={labelCls}>{t.reserve.guests} *</label>
                <input data-testid="res-guests" type="number" min="1" max="100" value={form.guests} onChange={set("guests")} className={inputCls} placeholder="2" />
              </div>

              <div>
                <label className={labelCls}>{t.reserve.occasion}</label>
                <input data-testid="res-occasion" value={form.occasion} onChange={set("occasion")} className={inputCls} placeholder={t.reserve.occasionPh} />
              </div>
              <div>
                <label className={labelCls}>{t.reserve.notes}</label>
                <input data-testid="res-notes" value={form.notes} onChange={set("notes")} className={inputCls} placeholder={t.reserve.notesPh} />
              </div>

              <div className="md:col-span-2 mt-2">
                <button
                  data-testid="reservation-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-brand hover:bg-brand-light text-white font-body px-10 py-4 transition-colors disabled:opacity-60 hover:scale-[1.02] duration-300"
                >
                  {loading ? t.reserve.submitting : t.reserve.submit}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
